import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, setTokens } from "../lib/api";
import { MapTrifold, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("admin@planner.com");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login" ? { email, password } : { name, email, password };
      const { data } = await api.post(url, body);
      setTokens(data.access_token, data.refresh_token);
      setUser(data);
      toast.success(`Welcome, ${data.name}!`);
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-11 h-11 bg-blue-500 border-2 border-black rounded-xl brutal-shadow flex items-center justify-center">
              <MapTrifold weight="bold" className="text-white" size={24} />
            </div>
            <span className="font-display font-bold text-2xl tracking-tight">ShiftRoute</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-2">
            {mode === "login" ? "Welcome back." : "Join the crew."}
          </h1>
          <p className="text-base font-medium text-slate-600 mb-8">
            Plan working hours, shifts and delivery routes in one place.
          </p>

          <div className="flex gap-2 mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMode(m);
                  setError("");
                }}
                data-testid={`tab-${m}`}
                className={`flex-1 border-2 border-black rounded-lg py-2 font-bold capitalize transition-all ${
                  mode === m ? "bg-yellow-200 brutal-shadow-sm" : "bg-white"
                }`}
              >
                {m === "login" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-900">
                  Name
                </label>
                <input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1 w-full bg-white border-2 border-black rounded-lg px-4 py-3 font-medium focus:outline-none focus:brutal-shadow"
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-900">
                Email
              </label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full bg-white border-2 border-black rounded-lg px-4 py-3 font-medium focus:outline-none focus:brutal-shadow"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-900">
                Password
              </label>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full bg-white border-2 border-black rounded-lg px-4 py-3 font-medium focus:outline-none focus:brutal-shadow"
                placeholder="••••••••"
              />
            </div>
            {mode === "register" && (
              <p className="text-xs font-medium text-slate-500">
                New accounts are created as <b>standard users</b> with view-only
                access. An admin can grant more access later.
              </p>
            )}

            {error && (
              <div
                data-testid="auth-error"
                className="bg-red-200 border-2 border-black rounded-lg px-4 py-2 text-sm font-bold"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full bg-blue-500 text-white border-2 border-black rounded-lg px-6 py-3 font-bold brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight weight="bold" size={18} />
            </button>
          </form>

          <p className="mt-6 text-sm font-medium text-slate-500">
            Demo admin: <b>admin@planner.com</b> / <b>admin123</b>
          </p>
        </div>
      </div>

      {/* hero side */}
      <div className="hidden lg:block relative border-l-2 border-black overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7363096/pexels-photo-7363096.jpeg"
          alt="Delivery driver"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-blue-600/20" />
        <div className="absolute bottom-8 left-8 right-8 bg-white border-2 border-black rounded-xl brutal-shadow-lg p-6">
          <p className="font-display text-2xl font-black tracking-tight">
            From 07:00 to 19:00, perfectly planned.
          </p>
          <p className="font-medium text-slate-600 mt-1">
            Two shifts. One team. Routes that just work.
          </p>
        </div>
      </div>
    </div>
  );
}
